import { useEffect, useState } from "react";
import {
  ArchiveDocument,
  CompanySpecificProduct,
  CreateCompanySpecificProductDto,
  DocumentType,
  Image,
  PropertyConfigDto,
  PropertyDto,
} from "@/types";
import { MIME_TYPES } from "@mantine/dropzone";
import { fileToBase64Raw, getFileTypeFromFile } from "@/utils/FileUtils";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Grid } from "@mantine/core";
import { SettingsFormContent, SettingsFormCTAs } from "../../pages/Settings";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useGetAllSalesItemsByLanguageId, useGetDynamicPropertiesForProduct } from "@/services/Product";
import { useGetDocumentsFromString } from "@/services/Documents";
import ImageCarousel from "../../molecules/ImageCarousel";

import Select from "@/components/atoms/Select";
import CTA from "@/components/atoms/CTA";
import TextInput from "@/components/atoms/TextInput";
import SelectProductBoxType from "../../molecules/SelectInputs/SelectProductBoxType";
import NumberInput from "@/components/atoms/NumberInput";
import Dropzone from "../../molecules/Dropzone";

type PropertyField = {
  selectedValueId: string | null; // selected value for RHF
  key: string; // unique key (from property index)
  label: string;
  properties: PropertyDto[];
  config: PropertyConfigDto;
};
type FormValues = {
  selectedSalesItemId: string | null;
  properties: PropertyField[];
  companySpecificProductName: string;
  kgPerMeter: string | null;
  kgPerM3: string | null;
  kgPerStk: string | null;
  boxTypeId: string | null;
};

interface DynamicProductFormProps {
  onSubmitMutation: (data: CreateCompanySpecificProductDto) => void;
  isMutating: boolean;
  disableForm?: boolean;
  defaultSelectedProduct?: CompanySpecificProduct | null;
  setDefaultSelectedProduct?: (product: CompanySpecificProduct | null) => void;
  defaultSelectedSalesItemProperties?: PropertyDto[];
  onClear?: () => void;
  clearable?: boolean;
}

const DynamicProductForm = ({
  onSubmitMutation,
  isMutating,
  disableForm,
  defaultSelectedProduct,
  setDefaultSelectedProduct,
  defaultSelectedSalesItemProperties,
  onClear,
  clearable = false,
}: DynamicProductFormProps) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [localImages, setLocalImages] = useState<File[]>([]);
  const [localAttachments] = useState<File[]>([]);

  const { data: salesItemsData, isLoading: isLoadingSalesItems } = useGetAllSalesItemsByLanguageId(
    user?.language.id as number,
    {
      enabled: !!user?.language.id,
    }
  );

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    mode: "onSubmit",
  });

  const { fields } = useFieldArray({
    control,
    name: "properties",
  });

  const selectedSalesItemId = watch("selectedSalesItemId");

  const { data: productPropertiesData, isFetching: isFetchingProductProperties } = useGetDynamicPropertiesForProduct(
    selectedSalesItemId ? Number(selectedSalesItemId) : 0,
    user?.language.id as number,
    {
      enabled: !!selectedSalesItemId && !!user?.language.id,
    }
  );

  const { images: existingImages, pending: isLoadingImages } = useGetDocumentsFromString(
    defaultSelectedProduct?.images ?? [],
    currentCompany?.companyId,
    !!defaultSelectedProduct && (defaultSelectedProduct.images?.length ?? 0) > 0
  );

  const {
    images: existingAttachmentImages,
    files: existingAttachmentFiles,
    pending: isLoadingAttachments,
  } = useGetDocumentsFromString(
    defaultSelectedProduct?.attachments ?? [],
    currentCompany?.companyId,
    !!defaultSelectedProduct && (defaultSelectedProduct.attachments?.length ?? 0) > 0
  );
  useEffect(() => {
    setLocalImages([]);
    if (!defaultSelectedProduct) return;

    setValue("selectedSalesItemId", defaultSelectedProduct.salesItemId.toString(), { shouldDirty: false });
    setValue("companySpecificProductName", defaultSelectedProduct.companySpecificProductName, { shouldDirty: false });
    setValue("kgPerMeter", defaultSelectedProduct.kgPerMeter?.toString() ?? null, { shouldDirty: false });
    setValue("kgPerM3", defaultSelectedProduct.kgPerM3?.toString() ?? null, {
      shouldDirty: false,
    });
    setValue("kgPerStk", defaultSelectedProduct.kgPerStk?.toString() ?? null, {
      shouldDirty: false,
    });
    setValue("boxTypeId", defaultSelectedProduct.boxTypeId?.toString() ?? null, { shouldDirty: false });
  }, [defaultSelectedProduct, setValue]);

  useEffect(() => {
    if (!productPropertiesData?.length) return;

    const preSelectedProperties = defaultSelectedSalesItemProperties ?? defaultSelectedProduct?.properties;

    reset({
      selectedSalesItemId: getValues("selectedSalesItemId"),
      companySpecificProductName: getValues("companySpecificProductName"),
      kgPerMeter: getValues("kgPerMeter"),
      kgPerM3: getValues("kgPerM3"),
      kgPerStk: getValues("kgPerStk"),
      boxTypeId: getValues("boxTypeId"),
      properties: productPropertiesData.map((property) => {
        const preSelected =
          preSelectedProperties?.find((preProp) => property.properties.some((prop) => prop.id === preProp.id)) ?? null;

        return {
          key: property.config.translatedLabel.toString(),
          selectedValueId: preSelected ? preSelected.id.toString() : null,
          label: property.config.translatedLabel,
          properties: property.properties,
          config: property.config,
        };
      }),
    });
  }, [productPropertiesData, defaultSelectedSalesItemProperties, defaultSelectedProduct?.properties, reset, getValues]);

  const handleClear = () => {
    reset({
      selectedSalesItemId: null,
      companySpecificProductName: "",
      kgPerMeter: null,
      kgPerM3: null,
      kgPerStk: null,
      boxTypeId: null,
      properties: [],
    });
    setDefaultSelectedProduct?.(null);
    onClear?.();
  };

  const onSubmit = async (formData: FormValues) => {
    if (!selectedSalesItemId) return;

    const selectedFullPropertyValues = productPropertiesData?.map((property) => {
      return property.properties.find((x) =>
        formData.properties.find((y) => Number(y.selectedValueId) === Number(x.id))
      );
    });

    if (!selectedFullPropertyValues) return;

    const filteredFullPropertyValues = selectedFullPropertyValues.filter((prop) => prop !== undefined);

    //Uppercase is done to satisfy backend
    const propertiesUpperCased = filteredFullPropertyValues.map((prop) => ({
      ...prop,
      value: prop?.value.toUpperCase(),
    }));

    if (!currentCompany) return;
    const base64Images: Image[] = await Promise.all(
      localImages.map(async (image) => {
        const base64File = await fileToBase64Raw(image as File);
        return {
          documentType: "IMAGE" as DocumentType,
          fileType: getFileTypeFromFile(image as File),
          image: base64File,
        };
      })
    );
    const base64Attachments: ArchiveDocument[] = await Promise.all(
      localAttachments.map(async (attachment) => {
        const base64File = await fileToBase64Raw(attachment as File);
        return {
          documentType: "ATTACHMENT" as DocumentType,
          fileType: getFileTypeFromFile(attachment as File),
          document: base64File,
          languageId: currentCompany.companyLanguageId,
          companyIds: [currentCompany.companyId],
          companyLanguageId: currentCompany.companyLanguageId,
          documentTitle: attachment.name,
        };
      })
    );

    const request: CreateCompanySpecificProductDto = {
      companyId: currentCompany.companyId,
      companySpecificProductName: formData.companySpecificProductName,
      salesItemId: Number(selectedSalesItemId),
      properties: propertiesUpperCased,
      companySpecificProductNetwork: [],
      images: base64Images,
      attachments: base64Attachments,
      kgPerMeter: formData.kgPerMeter ? Number(formData.kgPerMeter) : null,
      kgPerM3: formData.kgPerM3 ? Number(formData.kgPerM3) : null,
      kgPerStk: formData.kgPerStk ? Number(formData.kgPerStk) : null,
      boxTypeId: formData.boxTypeId ? Number(formData.boxTypeId) : null,
      originalProductNumber: null,
    };
    onSubmitMutation(request);
  };

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        key={defaultSelectedProduct ? defaultSelectedProduct.id : "new-product-form"}
      >
        <SettingsFormContent>
          <Grid>
            <Grid.Col span={6}>
              <Controller
                control={control}
                name="companySpecificProductName"
                rules={{
                  required: t("validation:companySpecificProductNameRequired"),
                }}
                render={({ field }) => (
                  <TextInput
                    withAsterisk
                    disabled={disableForm}
                    label={t("product:product")}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.companySpecificProductName?.message}
                    data-testid="product-name-input"
                  />
                )}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <Controller
                control={control}
                name="selectedSalesItemId"
                rules={{ required: t("validation:salesItemRequired") }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t("product:productType")}
                    disabled={disableForm}
                    withAsterisk
                    name={"salesItemId"}
                    role={"textbox"}
                    loading={isLoadingSalesItems}
                    data-testid="sales-item-select"
                    data={salesItemsData?.map((item) => ({
                      value: item.salesItemId.toString(),
                      label: item.salesItemTranslated,
                    }))}
                    value={value?.toString() ?? null}
                    onChange={(selectedId) => {
                      onChange(selectedId);
                    }}
                    error={errors.selectedSalesItemId?.message}
                  />
                )}
              />
            </Grid.Col>
            {fields?.map((field, index) => {
              return (
                <Grid.Col span={6} key={field.config.translatedLabel}>
                  <Controller
                    control={control}
                    name={`properties.${index}.selectedValueId`}
                    rules={{
                      required: field.config.required ? t("validation:required") : undefined,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        // label={t(`enumerables:${field.config.translatedLabel.replace(/\s+/g, "").toUpperCase()}`)}
                        label={field.config.translatedLabel}
                        withAsterisk={field.config.required}
                        disabled={disableForm}
                        loading={isFetchingProductProperties}
                        data={field.properties?.map((option) => ({
                          value: option.id.toString(),
                          label: option.value,
                        }))}
                        value={value ?? null}
                        onChange={onChange}
                        error={errors.properties?.[index]?.selectedValueId?.message}
                      />
                    )}
                  />
                </Grid.Col>
              );
            })}
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Controller
                control={control}
                name="kgPerMeter"
                render={({ field: { onChange, value } }) => {
                  return (
                    <NumberInput
                      label={t("product:kgPerMeter")}
                      disabled={disableForm}
                      min={1}
                      onChange={onChange}
                      value={value ?? undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Controller
                control={control}
                name="kgPerM3"
                render={({ field: { onChange, value } }) => {
                  return (
                    <NumberInput
                      label={t("product:kgPerM3")}
                      disabled={disableForm}
                      min={1}
                      onChange={onChange}
                      value={value ?? undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6, sm: 4 }}>
              <Controller
                control={control}
                name="kgPerStk"
                render={({ field: { onChange, value }, fieldState }) => {
                  return (
                    <NumberInput
                      label={t("product:kgPerStk")}
                      disabled={disableForm}
                      min={1}
                      onChange={onChange}
                      value={value ?? undefined}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, xs: 6 }}>
              <Controller
                control={control}
                name="boxTypeId"
                render={({ field: { onChange, value }, fieldState }) => {
                  return (
                    <SelectProductBoxType
                      id="size-id"
                      label={t("product:boxType")}
                      disabled={disableForm}
                      valueKey={"id"}
                      labelKey={"description"}
                      onChange={(value) => onChange(value ? Number(value) : null)}
                      value={value != null ? String(value) : null}
                      error={fieldState.error?.message ? t(fieldState.error.message) : undefined}
                    />
                  );
                }}
              />
            </Grid.Col>

            {defaultSelectedProduct ? (
              <>
                {(existingImages.length > 0 || isLoadingImages) && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <ImageCarousel
                      images={existingImages.filter((img) => img !== undefined)}
                      isLoading={isLoadingImages}
                      height={150}
                      imagesPrSlide={3}
                    />
                  </Grid.Col>
                )}
                {(existingAttachmentImages.length > 0 ||
                  existingAttachmentFiles.length > 0 ||
                  isLoadingAttachments) && (
                  <Grid.Col span={{ base: 12, md: 6 }}>
                    <ImageCarousel
                      images={existingAttachmentImages.filter((img) => img !== undefined)}
                      isLoading={isLoadingAttachments}
                      height={150}
                      imagesPrSlide={3}
                    />
                  </Grid.Col>
                )}
              </>
            ) : (
              !disableForm && (
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Dropzone
                    type="button"
                    origin="local"
                    files={localImages}
                    setFiles={setLocalImages}
                    buttonLabel={t("product:uploadImages")}
                    accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
                    showAsImageCarousel={true}
                  />
                </Grid.Col>
              )
            )}
          </Grid>
        </SettingsFormContent>
        <SettingsFormCTAs>
          {defaultSelectedProduct && clearable && (
            <CTA type="button" size="lg" variant="outline" onClick={handleClear} data-testid="clear-btn">
              {t("actions:clear")}
            </CTA>
          )}
          <CTA
            type="submit"
            size="lg"
            loading={isMutating}
            disabled={disableForm || !!defaultSelectedProduct}
            data-testid="submit-btn"
          >
            {t("actions:save")}
          </CTA>
        </SettingsFormCTAs>
      </form>
    </>
  );
};

export default DynamicProductForm;
